import { placeholderImageSrc } from '@/utils/constants';
// eslint-disable-next-line no-restricted-imports -- raw token read kept until this site is moved to theme.semantics
import { DS_BREAKPOINT, DS_COLOR } from '@calm/evexia-design-tokens';
import {
    Box,
    ButtonBase,
    ButtonBaseProps,
    Theme,
    useTheme,
    alpha,
    Breakpoint,
} from '@rhg/libs/material';
import {
    NumberByBreakpoint,
    sxAppend,
    ValueByBreakpoint,
} from '@rhg/libs/material/sx';
import Image, { StaticImageData } from '@rhg/libs/next/image';
import { ReactNode, useMemo } from 'react';
import {
    defaultCarouselCardWidth,
    defaultCarouselCardWidthMobile,
} from '../carousel/utils';
import {
    ImageDecorationOverlay,
    ImageDecorationProps,
} from './image-decoration';
import { ShowIconsOnFocused } from './types';

const TRANSITION_DURATION = 250;
const TRANSITION_EASING = 'ease-in';

const imageContainerStyle = {
    display: 'flex',
    '& img': {
        width: '100%',
    },
    overflow: 'hidden',
};

const styleImageContainer = imageContainerStyle;
const styleImageContainer11 = {
    ...imageContainerStyle,
    '& img': {
        ...imageContainerStyle['& img'],
        aspectRatio: '1',
    },
};
const styleImageContainer32 = {
    ...imageContainerStyle,
    '& img': {
        ...imageContainerStyle['& img'],
        aspectRatio: '3 / 2',
    },
};
const styleImageContainerFill = {
    ...imageContainerStyle,
    height: '100%',
    justifyContent: 'center',
    alignItems: 'center',
};
const styleImageContainerFill11 = {
    ...styleImageContainerFill,
    '& img': {
        ...imageContainerStyle['& img'],
        aspectRatio: '1',
        height: 'auto',
    },
};
const styleImageContainerFill32 = {
    ...styleImageContainerFill,
    '& img': {
        ...imageContainerStyle['& img'],
        aspectRatio: '3 / 2',
        height: 'auto',
    },
};
const styleImageOuterContainer = { position: 'relative' };
const styleImageOuterContainerFill = {
    position: 'relative',
    height: '100%',
    width: '100%',
};
const styleImageOverlay = {
    position: 'absolute',
    width: '100%',
    height: '100%',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 1,
};

const styleIconFocus = { display: 'flex' };
const styleButton = { display: 'inline', width: '100%' };
const styleFocusedOverlay = (theme: Theme) => ({
    transition: theme.transitions.create(['background-color'], {
        duration: TRANSITION_DURATION,
        easing: TRANSITION_EASING,
    }),
    backgroundColor: alpha(DS_COLOR.BLACK_100P, 0.4),
});
const styleNotFocusedOverlay = (theme: Theme) => ({
    transition: theme.transitions.create(['background-color'], {
        duration: TRANSITION_DURATION,
        easing: TRANSITION_EASING,
    }),
    backgroundColor: 'transparent',
});
const styleFocusedImage = (theme: Theme) => ({
    transition: theme.transitions.create(['transform'], {
        duration: TRANSITION_DURATION,
        easing: TRANSITION_EASING,
    }),
    transform: 'scale(1.05)',
});
const styleNotFocusedImage = (theme: Theme) => ({
    transition: theme.transitions.create(['transform'], {
        duration: TRANSITION_DURATION,
        easing: TRANSITION_EASING,
    }),
    transform: 'scale(1)',
});

interface BaseElementProps extends Partial<ButtonBaseProps> {
    component?: React.ElementType;
    href?: string;
}

function BaseElement({
    children,
    onClick,
    sx,
    trackButton,
    onMouseEnter,
    onMouseLeave,
    onFocus,
    onBlur,
    href,
    ...buttonBaseProps
}: BaseElementProps) {
    const sxProp = { ...styleButton, ...(sx ?? {}) };
    return onClick || href ? (
        <ButtonBase
            onMouseEnter={onMouseEnter}
            onMouseLeave={onMouseLeave}
            onFocus={onFocus}
            onBlur={onBlur}
            onClick={onClick}
            href={href}
            aria-label={buttonBaseProps?.['aria-label']}
            trackButton={trackButton || 'NotMapped'}
            sx={sxProp}
            {...buttonBaseProps}
        >
            {children}
        </ButtonBase>
    ) : (
        <Box sx={sx}>{children}</Box>
    );
}

const isStaticImageData = (src?: ImageSrc): src is StaticImageData => {
    return (src as StaticImageData).width !== undefined;
};

function styleImage({
    fillContainer,
    ratio,
    bezel,
    imageSideRadiusEnabled,
    maxHeight,
}: {
    fillContainer: FocusableOverlaidImageProps['fillContainer'];
    ratio: FocusableOverlaidImageProps['ratio'];
    bezel: FocusableOverlaidImageProps['bezel'];
    imageSideRadiusEnabled: FocusableOverlaidImageProps['imageSideRadiusEnabled'];
    maxHeight: FocusableOverlaidImageProps['maxHeight'];
}) {
    // Select base style based on fillContainer and ratio
    let baseImageStyle;
    if (fillContainer) {
        if (ratio === '1:1') {
            baseImageStyle = styleImageContainerFill11;
        } else if (ratio === '3:2') {
            baseImageStyle = styleImageContainerFill32;
        } else {
            baseImageStyle = styleImageContainerFill;
        }
    } else {
        if (ratio === '1:1') {
            baseImageStyle = styleImageContainer11;
        } else if (ratio === '3:2') {
            baseImageStyle = styleImageContainer32;
        } else {
            baseImageStyle = styleImageContainer;
        }
    }

    return sxAppend(
        baseImageStyle,
        bezel && {
            borderTopLeftRadius: `${bezel}px`,
            borderBottomLeftRadius: `${bezel}px`,
            borderTopRightRadius: imageSideRadiusEnabled ? `${bezel}px` : 0,
            borderBottomRightRadius: imageSideRadiusEnabled ? `${bezel}px` : 0,
        },
        maxHeight && { maxHeight }
    );
}

function ImageFocusOverlay({
    iconFocus,
    isFocused,
    bezel,
    imageSideRadiusEnabled,
    transitionStyles,
}: {
    iconFocus?: ReactNode;
    isFocused: boolean;
    bezel: number;
    imageSideRadiusEnabled: boolean;
    transitionStyles: {
        focusedOverlay: ReturnType<typeof styleFocusedOverlay>;
        notFocusedOverlay: ReturnType<typeof styleNotFocusedOverlay>;
    };
}) {
    return (
        <Box
            sx={sxAppend(
                styleImageOverlay,
                bezel && {
                    borderTopLeftRadius: `${bezel}px`,
                    borderBottomLeftRadius: `${bezel}px`,
                    borderTopRightRadius: imageSideRadiusEnabled
                        ? `${bezel}px`
                        : 0,
                    borderBottomRightRadius: imageSideRadiusEnabled
                        ? `${bezel}px`
                        : 0,
                },
                isFocused
                    ? transitionStyles.focusedOverlay
                    : transitionStyles.notFocusedOverlay
            )}
        >
            {isFocused && iconFocus && (
                <Box sx={styleIconFocus}>{iconFocus}</Box>
            )}
        </Box>
    );
}

// Fallback `sizes` for a non-fillContainer card image that renders without an
// explicit width. Without a `sizes`, next/image assumes 100vw and over-fetches
// (e.g. a 400px card pulling a 1080px rendition). Rooted in design tokens + the
// carousel card-width constants (no magic numbers): below the MEDIUM breakpoint
// request the mobile card width, above it the desktop card width. Full-bleed
// images (fillContainer) intentionally keep 100vw — they can span the viewport.
const DEFAULT_CARD_IMAGE_SIZES = `(max-width: ${DS_BREAKPOINT.MEDIUM}px) ${defaultCarouselCardWidthMobile}px, ${defaultCarouselCardWidth}px`;

function ImageComponent({
    src,
    alt,
    isFocused,
    width,
    priority,
    fillContainer,
    sizes,
}: { src: ImageSrc } & Pick<
    FocusableOverlaidImageProps,
    'alt' | 'isFocused' | 'width' | 'priority' | 'fillContainer' | 'sizes'
>) {
    const theme = useTheme();
    const transitionStyles = useMemo(
        () => ({
            focusedOverlay: styleFocusedOverlay(theme),
            notFocusedOverlay: styleNotFocusedOverlay(theme),
            focusedImage: styleFocusedImage(theme),
            notFocusedImage: styleNotFocusedImage(theme),
        }),
        [theme]
    );

    const style = {
        ...(isFocused
            ? transitionStyles.focusedImage
            : transitionStyles.notFocusedImage),
        objectFit: 'cover',
        height: '100%',
        width: '100%',
    } as const;

    const breakpointValues = theme.breakpoints.values;
    const nextImageSizes = useMemo(() => {
        // An explicit `sizes` always wins — including for `fillContainer`
        // images (e.g. a full-bleed banner that only occupies ~50% of the
        // layout and shouldn't fetch a 100vw rendition).
        if (sizes) return sizes;
        if (!width) {
            // No explicit width: full-bleed images may truly span the viewport;
            // everything else is a bounded card, so cap it instead of 100vw.
            return fillContainer ? undefined : DEFAULT_CARD_IMAGE_SIZES;
        }
        if (typeof width === 'number') {
            return `${width}px`;
        }
        if (typeof width === 'string') {
            return width;
        }
        const explicitEntries = (
            Object.entries(width) as [Breakpoint, string | number][]
        )
            .map(([breakpoint, value]) => {
                const size = typeof value === 'number' ? `${value}px` : value;
                return `(min-width: ${breakpointValues[breakpoint]}) ${size}`;
            })
            .join(', ');
        return `${explicitEntries}, 100vw`;
    }, [width, breakpointValues, fillContainer, sizes]);

    if (isStaticImageData(src)) {
        return (
            <Image
                src={src}
                alt={alt}
                style={style}
                sizes={nextImageSizes}
                priority={priority}
            />
        );
    }
    return (
        <Image
            src={src}
            alt={alt}
            width={1024}
            height={1024}
            style={style}
            sizes={nextImageSizes}
            priority={priority}
        />
    );
}

type ImageSrc = string | StaticImageData;

export type FocusableOverlaidImageProps = {
    src?: ImageSrc;
    alt: string;
    /** Array of image decoration props with positions */
    decorations?: ImageDecorationProps[];
    isFocused?: boolean;
    bezel?: number;
    iconFocus?: ReactNode;
    showIconsOnFocused?: ShowIconsOnFocused;
    ratio?: '1:1' | '3:2';
    width?: Partial<ValueByBreakpoint<number | string>>;
    maxHeight?: NumberByBreakpoint;
    imageSideRadiusEnabled?: boolean;
    fillContainer?: boolean;
    /** Eager-load + high fetch priority (next/image `priority`). Use for the
     *  above-the-fold hero/LCP image only. */
    priority?: boolean;
    /** Explicit next/image `sizes`. Takes precedence over `width` and the
     *  carousel-card default — including for `fillContainer` images. Pass this
     *  for non-carousel callers (banners, hero images) so they fetch a bounded
     *  rendition instead of 100vw / carousel card dimensions. */
    sizes?: string;
} & Partial<ButtonBaseProps>;
export default function FocusableOverlaidImage({
    src = placeholderImageSrc,
    alt,
    decorations,
    isFocused = false,
    bezel = 0,
    iconFocus,
    ratio,
    maxHeight,
    width,
    showIconsOnFocused,
    imageSideRadiusEnabled = true,
    fillContainer = false,
    priority,
    sizes,
    ...buttonBaseProps
}: FocusableOverlaidImageProps) {
    const theme = useTheme();

    const transitionStyles = useMemo(
        () => ({
            focusedOverlay: styleFocusedOverlay(theme),
            notFocusedOverlay: styleNotFocusedOverlay(theme),
            focusedImage: styleFocusedImage(theme),
            notFocusedImage: styleNotFocusedImage(theme),
        }),
        [theme]
    );

    const imageStyle = styleImage({
        fillContainer,
        ratio,
        bezel,
        imageSideRadiusEnabled,
        maxHeight,
    });

    return (
        <Box
            sx={
                fillContainer
                    ? styleImageOuterContainerFill
                    : styleImageOuterContainer
            }
        >
            <BaseElement
                {...buttonBaseProps}
                sx={sxAppend(
                    buttonBaseProps.sx,
                    fillContainer && { height: '100%' }
                )}
            >
                <ImageFocusOverlay
                    iconFocus={iconFocus}
                    isFocused={isFocused}
                    bezel={bezel}
                    imageSideRadiusEnabled={imageSideRadiusEnabled}
                    transitionStyles={transitionStyles}
                />
                <Box sx={imageStyle}>
                    <ImageComponent
                        src={src}
                        alt={alt}
                        isFocused={isFocused}
                        width={width}
                        priority={priority}
                        fillContainer={fillContainer}
                        sizes={sizes}
                    />
                </Box>
            </BaseElement>
            <ImageDecorationOverlay
                decorations={decorations}
                isFocused={isFocused}
                showIconsOnFocused={showIconsOnFocused}
            />
        </Box>
    );
}
